import { platformRouteByPath, platformRoutes } from './platformRoutes';
import { docEndpointBySlug, docEndpoints, docSectionById, docSectionIds } from './docsData';
import { useCases, useCaseMeta } from './ai/useCasesData';
import { alternativeBySlug, alternatives } from './alternativesData';
import { libraryPromptBySlug, libraryPrompts } from './PromptLibraryPage';
import promptLibraryJSON from '../server/seed/prompt_library.json';
import { toolBySlug, toolSlugs } from './tools/toolsRegistry';
import { spaceMeta, spaces } from './spaces/spacesRegistry';
import { stripDesktopAppBasePath } from '../lib/desktopPath';
import { evalSourceBySlug, evalSources } from './evals/evalData';
import blogPosts from './blog/blogMetadata.json';
import { cogShowcaseBySlug, cogShowcasePaths } from './cogs/cogShowcaseData';

export const HOME_PAGE_META = {
  title: 'app.nz | The agent cloud for building and shipping software',
  description:
    'Repos, models, GPUs, hosting, and editors on one account, on one credit balance.',
};

const STATIC_PAGE_META: Record<string, { title: string; description: string }> = {
  '/feed': {
    title: 'Creation feed — AI models, datasets, code and tutorials | app.nz',
    description: 'Follow AI builders, and search AI models, datasets, code artifacts, tutorials, research, and generated art on app.nz.',
  },
  '/mail': {
    title: 'Mail — a fast agentic inbox and transactional email studio | app.nz',
    description: 'Manage personal and connected inboxes, search with exact and Gobed semantic ranking, automate mail with agents, and build transactional email.',
  },
  '/apps': {
    title: 'Apps built on app.nz — games, tools, and community projects',
    description: 'Discover games, creative tools, education products, and developer apps built on app.nz. Deploy once and join the gallery.',
  },
  '/games/neon-relay': {
    title: 'Neon Relay — free browser arcade game | app.nz',
    description: 'Play Neon Relay free in your browser: dodge signal blocks, build your score, and race your personal best.',
  },
  '/games/orbit-words': {
    title: 'Orbit Words — free browser word game | app.nz',
    description: 'Connect letter planets into words and build a high-scoring constellation in this free app.nz browser puzzle.',
  },
  '/games/reword': {
    title: 'reWord — play the V5Games word puzzle | app.nz',
    description: 'Rearrange letters, solve clues, and play the revived reWord puzzle free on app.nz.',
  },
  '/games/big-multiplayer-chess': {
    title: 'Big Multiplayer Chess — connected app.nz arcade',
    description: 'Play the original Big Multiplayer Chess MMO through the app.nz games marketplace.',
  },
  '/games/word-smashing': {
    title: 'Word Smashing — connected app.nz arcade',
    description: 'Play the V5Games Word Smashing challenge through the app.nz games marketplace.',
  },
  '/games/multiplication-master': {
    title: 'Multiplication Master — connected app.nz arcade',
    description: 'Play the educational Multiplication Master maths game through the app.nz games marketplace.',
  },
  '/games/signal-orchard': {
    title: 'Signal Orchard — rhythm story game | app.nz',
    description: 'Tune coloured radio pulses, grow a bioluminescent signal tree, and recover the last messages from a vanished island.',
  },
  '/games/cartographers-kitchen': {
    title: "Cartographer's Kitchen — mapmaking cooking game | app.nz",
    description: 'Combine magical ingredients to reveal roads, reshape a fogged map, and cook your way to the lighthouse market.',
  },
  '/games/museum-after-midnight': {
    title: 'Museum After Midnight — stealth detective game | app.nz',
    description: 'Rearrange a moonlit collection, build a trap from contradictory clues, and expose the museum thief before midnight.',
  },
  '/docs': {
    title: 'Documentation | app.nz',
    description:
      'Documentation and HTTP API reference for cloud coding agents, the OpenAI-compatible model gateway, auth keys, and sites hosted on app.nz.',
  },
  '/investors': {
    title: 'Investors | app.nz',
    description:
      'app.nz for investors: what the platform is, what customers use today, the 10-year plan, a technical deep dive, security, and economics.',
  },
  '/investors/vision': {
    title: 'The 10-year vision | app.nz Investors',
    description:
      'Where software goes in the next decade, and what app.nz is building for it: the agent cloud, self-optimizing infrastructure, long-running agents, and self-maintaining software.',
  },
  '/investors/deep-dive': {
    title: 'Technical deep dive | app.nz Investors',
    description:
      'app.nz architecture, learned model routing, GPU placement across providers, security, unit economics, benchmarks, roadmap, and risks.',
  },
  '/addons': {
    title: 'Hosted Addons — databases, GPU search, auth, monitoring | app.nz',
    description:
      'Attach managed services to any app or agent in one command: gobed GPU vector search, PostgreSQL with pgvector HNSW and PostGIS, Mongo, memcache, auth, and AI monitoring. Env vars reach every runtime.',
  },
  '/addons/gobed': {
    title: 'gobed — GPU vector search addon with CAGRA indexes | app.nz',
    description:
      'GPU vector search as a service: int8 static embeddings (15 MB model, ~150K embeds/sec), flat/IVF/HNSW on CPU and cuVS CAGRA on GPU, behind three HTTP endpoints and one env var.',
  },
  '/addons/postgres': {
    title: 'Hosted PostgreSQL — HNSW vectors, PostGIS, graph traversal | app.nz',
    description:
      'Managed Postgres with pgvector and PostGIS enabled by default, recursive-CTE graph traversal, and hybrid retrieval APIs. One command attaches it to your app and agents as DATABASE_URL.',
  },
  '/products/papers': {
    title: 'Papers — semantic search over 725K ML papers and their code | app.nz',
    description:
      'A papers-with-code search engine in one Go binary. Free gobed semantic search over 725K papers, 613K code links, a live arXiv crawler, an embedded PDF reader, and figure-aware social cards at app.nz/papers.',
  },
  '/rl': {
    title: 'Hosted RL — train reinforcement learning policies on cloud GPUs | app.nz',
    description:
      'Pick a gymnasium, Atari, MuJoCo, LLM-RLHF, or robotics environment plus a PPO/DQN/GRPO baseline repo, train on per-second billed GPUs with live metrics, and deploy the policy as a scale-to-zero cog or serverless endpoint.',
  },
  '/enterprise': {
    title: 'Enterprise — teams, RBAC, audit logs, spend limits | app.nz',
    description:
      'Run app.nz with your team: organizations with roles and invites, audit logs, per-user spend limits, and API key management. The roadmap covers SAML SSO, SCIM, private networking, and SOC 2.',
  },
  '/security': {
    title: 'Security and trust — encryption, isolation, subprocessors | app.nz',
    description:
      'How app.nz protects your data: encryption in transit and at rest, hashed key storage, container and VM isolation, vulnerability reporting with safe harbor, data deletion, and the subprocessor list.',
  },
  '/settings': {
    title: 'Settings | app.nz',
    description:
      'Manage your profile, workspace members and roles, notifications, privacy controls, spend limit, and default GPU region.',
  },
  '/group-chat': {
    title: 'AI Group Chats — multi-character rooms | app.nz',
    description: 'Create a shared room with multiple AI characters, send one prompt, and get distinct responses that build on the same conversation.',
  },
  '/social': {
    title: 'Social scheduler — queue posts across networks | app.nz',
    description:
      'Compose once and schedule to X, LinkedIn, Reddit, Bluesky, and Mastodon; the desktop browser agent posts on time and saves screenshot proof.',
  },
  '/skills/market': {
    title: 'Skills marketplace — installable MCP servers and prompt packs | app.nz',
    description:
      'Browse and search MCP servers and prompt packs, get ready-to-paste config snippets, or one-click install to the app.nz desktop agent.',
  },
  '/agent': {
    title: 'Cloud coding agents | app.nz',
    description: platformRouteByPath['/agents'].description,
  },
  '/cli': {
    title: 'CLI — agents, deploys, and models from your terminal | app.nz',
    description:
      'Install one binary to run cloud coding agents, deploy apps and static sites to <name>.app.nz, stream chat from 100+ models, manage repos and CI, and script every endpoint. Watch a recorded session build and ship a game.',
  },
  '/browser-agent': {
    title: 'Browser Agent — an in-browser coding agent | app.nz',
    description:
      'A coding agent that runs in your browser tab: a tool-calling loop over the app.nz gateway that runs Python in Pyodide and searches the web, repos, and papers, with one-click escalation to a full cloud agent for real repos and shells.',
  },
  '/schedulers': {
    title: 'Agent schedulers — recurring autonomous work | app.nz',
    description: 'Schedule recurring coding-agent and automation runs, inspect run history, and manage cron-driven work from the app.nz cloud, CLI, and desktop app.',
  },
  '/agents-sdk': {
    title: 'Agents SDK — build tool-using agents | app.nz',
    description: 'Build and run tool-using agents against app.nz models and cloud workers with streaming output, durable tasks, and the same APIs used by the CLI and desktop app.',
  },
  '/auto-agents': {
    title: 'Auto agents — event-driven software automation | app.nz',
    description: 'Create autonomous agents that react to repository, deployment, and monitoring events, then inspect every run, tool call, artifact, and result.',
  },
  '/monitors': {
    title: 'Site and service monitoring | app.nz',
    description: 'Monitor websites, APIs, Git repositories, and hosted models with uptime checks, incident history, and agent-assisted remediation from app.nz.',
  },
  '/video-extension': {
    title: 'AI video extension tool | app.nz',
    description: 'Extend a video forward or backward with generative AI, preview the result in the browser, and run the same workflow through the app.nz media API.',
  },
  '/text-to-image': {
    title: 'Text-to-image generator and API | app.nz',
    description: 'Generate images from prompts with leading image models, compare output and pricing, and copy OpenAI-compatible API examples.',
  },
  '/textures': {
    title: 'Verified seamless texture library and generator | app.nz',
    description: 'Search and generate verified tileable textures with Z-Image: exact procedural synthesis, 2×2 seam proofs, background-priority inference, and cyclic similarity search.',
  },
  '/image-to-3d': {
    title: 'Image-to-3D generator | app.nz',
    description: 'Turn a reference image into a textured 3D model, preview it in the browser, and download the GLB through app.nz.',
  },
  '/text-to-3d': {
    title: 'Text-to-3D generator | app.nz',
    description: 'Describe a prop or object, generate a textured 3D asset, inspect it in an interactive viewer, and download the GLB.',
  },
  '/playground': {
    title: 'AI model playground | app.nz',
    description: 'Try and compare app.nz model routes in the browser, tune inputs, inspect outputs, and move working requests into production with one API.',
  },
  '/spaces/creator-lab': {
    title: '3D Creator Lab — generate and edit game assets | app.nz',
    description: 'Generate, inspect, edit, and export 3D game assets in one browser workspace with app.nz models, procedural tools, and downloadable GLB output.',
  },
  '/spaces/blender-lab': {
    title: 'Blender Agent Lab — automate 3D scenes | app.nz',
    description: 'Use an AI agent and reproducible Blender recipes to create, modify, render, and export 3D scenes through the app.nz desktop and cloud toolchain.',
  },
  '/spaces/unreal-lab': {
    title: 'Unreal Agent Lab — build game worlds | app.nz',
    description: 'Plan and automate Unreal Engine scenes, assets, materials, and gameplay tasks with app.nz agents, desktop tools, and reproducible project recipes.',
  },
  '/props': {
    title: 'Props Studio — text to 3D game props | app.nz',
    description:
      'Generate 3D game props from text: prompt to reference image to textured GLB via pixal3d image-to-3D. Build a library of weapons, shields, hats, hair, and costumes ready to equip on VRM characters.',
  },
  '/vrm-editor': {
    title: 'VRM Character Editor — blend animations, equip props | app.nz',
    description:
      'Load a VRM avatar, blend walk, run, wave, and attack animations, attach AI-generated props to hands, head, and back, swap hair and tint costumes — all in the browser.',
  },
  '/worlds': {
    title: 'AI Panorama & 3D World Studio | app.nz',
    description:
      'Turn one image into a seamless 360 panorama or spatial world, preview it in an interactive WebGL viewer, and visit it with a Netwrck VRM companion.',
  },
  '/3d-packing': {
    title: '3D Packing Agent — will it fit? AI bin packing in 3D | app.nz',
    description:
      'Plan loads for vans, trailers, containers, and storage units with AI-estimated item sizes, 3D bin-packing, interactive layouts, and practical loading guidance.',
  },
  '/skills': {
    title: 'Agent skills — searchable skill library | app.nz',
    description: platformRouteByPath['/skills'].description,
  },
  '/mcp-servers': {
    title: 'MCP Server Directory — searchable index of Model Context Protocol servers | app.nz',
    description:
      'Search 120+ MCP servers for Claude, Codex, and any agent: GitHub, Gmail, fal, Canva, Hugging Face, Stripe, Notion, and more. Filter by category and pricing, with links to every provider.',
  },
  '/integrations': {
    title: 'Integrations — chat with cloud coding agents from Slack, Jira, GitHub and more | app.nz',
    description:
      'Connect Slack, Microsoft Teams, Jira, Linear, Notion, GitHub, GitLab, Azure DevOps, Bitbucket, JetBrains, Xcode, deeplinks, and SDKs to app.nz cloud coding agents.',
  },
  '/datasets': {
    title: 'Dataset Studio — chat to a dataset, get charts | app.nz',
    description:
      'Hosted datasets, charted in plain English. Open one, preview the rows, and ask for any chart: a built-in data analyst agent writes the query and returns an interactive Plotly figure.',
  },
  '/notebooks': {
    title: 'Notebooks — marimo in your browser or on cloud GPUs | app.nz',
    description:
      'Hosted marimo notebooks: run Python free in your browser on Pyodide, or start a per-minute CPU/GPU machine that stops when idle. Load app.nz datasets directly and fork Kaggle-style examples.',
  },
  '/search': {
    title: 'Fast web search — live, indexed, and agentic | app.nz',
    description:
      'Search the live web and a compact gobed-ranked page index, open cached readable pages, or hand the query to a search agent.',
  },
  '/search/web': { title: 'Live web search | app.nz', description: 'Fast Google-style live web results through app.nz search providers.' },
  '/search/index': { title: 'Compact indexed web search | app.nz', description: 'Search disk-efficient snippets from the WebFiddle cache, reranked by gobed and linked to readable cached pages.' },
  '/search/agent': { title: 'Web search agent | app.nz', description: 'Run a generic search agent across the compact index and live web with deduplicated sources.' },
  '/deep-research': { title: 'Deep Research — cited multi-source reports | app.nz', description: 'Plan, search the live web, compact index and papers, read cached pages, verify sources, and produce a cited report.' },
  '/papers': {
    title: 'Papers — semantic research search | app.nz',
    description: 'Search papers, methods, datasets, citations, and linked GitHub code free in the app.nz Papers workspace.',
  },
  '/draw': {
    title: 'Draw — whiteboard for diagrams and sketches | app.nz',
    description:
      'A free Excalidraw whiteboard: sketch diagrams, wireframes and ideas, save drawings to your library, share them by link or with your team, and export PNGs.',
  },
  '/diffusion': {
    title: 'Diffusion canvas — infinite AI image canvas | app.nz',
    description:
      'An infinite canvas for diffusion models: generate images into 512 or 1024 frames, expand edges with outpainting, rerender any region with a prompt, and animate tiles into video.',
  },
  '/livedraw': {
    title: 'Live Draw — sketch to AI render in real time | app.nz',
    description:
      'Draw with pressure-sensitive pen dynamics and watch a fast diffusion model rerender your sketch live. Drive it with a prompt, an image, or both.',
  },
  '/sheets': {
    title: 'Sheets — AI spreadsheet and forecasting | app.nz',
    description:
      'Edit spreadsheets in the browser with formulas, rich charts, XLSX and CSV import/export, an AI sheet agent, and Chronos-2 time-series forecasting.',
  },
  '/audio': {
    title: 'Audio editor — record, trim, and export in the browser | app.nz',
    description:
      'A free browser audio editor: upload or record, edit the waveform with trim, fade, gain, normalize and reverse, then export WAV or save to your library.',
  },
  '/image': {
    title: 'Image editor — layers, brushes, and adjustments | app.nz',
    description:
      'A free Photopea-style browser image editor: layers with blend modes, crop, brush, text and shapes, color adjustments, and PNG/JPEG/WebP export.',
  },
  '/slides': {
    title: 'Slides — browser presentation editor with PPTX export | app.nz',
    description:
      'Build presentations in the browser: drag-and-drop slide canvas, templates and themes, present mode, autosave to your library, and PowerPoint (.pptx) export.',
  },
  '/vector': {
    title: 'Vector editor — SVG shapes, pen tool, and layers | app.nz',
    description:
      'A free browser SVG editor: draw shapes and bezier paths, edit fills and strokes, manage layers and groups, then export SVG or PNG.',
  },
  '/artifacts': {
    title: 'Library — your drawings, PDFs and generated content | app.nz',
    description:
      'Everything you generate on app.nz in one library: whiteboard drawings, markdown-generated PDFs and saved images, private by default with link and team sharing.',
  },
  '/dashboards': {
    title: 'Dashboards — native dataset dashboards | app.nz',
    description:
      'Build native app.nz dashboards over uploaded datasets with tables, charts, metrics, markdown panels, and reusable dataframe-style transform steps.',
  },
  '/repos': {
    title: 'Repository Hub | app.nz',
    description: 'Host repositories, branches, commits, issues, and pull requests for app.nz agents and deploys.',
  },
  '/models': {
    title: 'Model directory | app.nz',
    description:
      'Browse every model routable through the app.nz OpenAI-compatible gateway: pricing, context windows, capabilities, and usage. One API key, automatic failover.',
  },
  '/evals': {
    title: 'AI Model Evals — benchmarks to working inference | app.nz',
    description:
      'Use Artificial Analysis, Arena, LiveBench, SWE-bench, and open leaderboards to shortlist AI models, then run hosted gateway models or tested ComfyUI GPU workflows.',
  },
  '/cogs': {
    title: 'Cog Studio — GPU model hosting | app.nz',
    description: 'Deploy any Cog container as a scale-to-zero GPU endpoint on app.nz with published usage-based pricing and an auto-generated UI.',
  },
  '/cogs/examples': {
    title: 'Open Cog examples — verified input/output recipes | app.nz',
    description: 'Browse canonical Cog showcases with default inputs, hardware tiers, and side-by-side outputs. Deploy the same template through HTTP, CLI, desktop, or MCP.',
  },
  '/training': {
    title: 'Training — hosted fine-tuning | app.nz',
    description:
      'Fine-tune open text, image, video, and time-series models with LoRA or full training on per-second billed GPUs, then deploy to a dedicated endpoint.',
  },
  '/comfy': {
    title: 'Comfy Studio — run ComfyUI workflows on app.nz GPUs',
    description: 'Paste a ComfyUI workflow, name your inputs, and run it on app.nz GPUs without managing a server.',
  },
  '/media-optimizer': {
    title: 'Media Optimizer — dynamic images and AV1 video | app.nz',
    description:
      'Cloudflare-style dynamic image resizing, responsive WebP/AVIF/JPEG variants, and AV1-first video ladders backed by app.nz artifacts, R2 storage, the CLI, the API, and Cog hosting.',
  },
  '/image-cdn': {
    title: 'Image CDN — dynamic resizing on app.nz',
    description:
      'Serve best-size images from artifact IDs with automatic AVIF/WebP/JPEG negotiation and private artifact access controls on app.nz.',
  },
  '/builds': {
    title: 'Build Studio — container CI + private registry | app.nz',
    description: 'Build container images on our autoscaling builders and push them to your private registry. Nothing is public.',
  },
  '/ci': {
    title: 'CI Control Room — provider-compatible pipelines | app.nz',
    description:
      'Create GitHub Actions, Buildkite, CircleCI, and GitLab CI compatible pipeline definitions, trigger runs, and inspect CI stages and logs on app.nz.',
  },
  '/spaces': {
    title: 'Model Spaces — image, video, 3D & audio playgrounds | app.nz',
    description:
      'Browse hosted model spaces for text-to-image, video generation, image-to-3D, editing, and sound effects, each with a playground and OpenAI-compatible API.',
  },
  '/spaces/fast-vfx': {
    title: 'FastVFX Cog Space — video in, video out | app.nz',
    description: 'A real lee101/fastvfx Cog hosted on app.nz: send in a video, run a GPU Cog endpoint, and get a processed video back.',
  },
  '/spaces/media-services': {
    title: 'Media Services — transcription and video encoding APIs | app.nz',
    description: 'Transcribe audio and re-encode video through app.nz GPU Cog hosting, including AV1/WebM output backed by R2 CDN storage.',
  },
  '/spaces/splat': {
    title: 'Splat Capture — photos to gaussian splat | app.nz',
    description:
      'Turn 2-64 photos into a splat you can orbit in the browser, plus point cloud and textured GLB mesh exports. Flat per-capture pricing.',
  },
  '/spaces/fast-video': {
    title: 'Fast Video — accelerated LTX-2.3 image-to-video | app.nz',
    description:
      'LTX-2.3 text- and image-to-video with native audio, up to 4K and 20 seconds — billed per second of output at a fraction of API prices.',
  },
  '/spaces/sam-mask-adjust': {
    title: 'SAM Mask Adjustment Cog Space | app.nz',
    description:
      'A self-hosted SAM2 Cog space for selecting objects on the first video frame and applying color, blur, and sharpness edits to the mask or background.',
  },
  '/spaces/audio-to-audio': {
    title: 'Audio to Audio — live voice conversation with Audex-2B | app.nz',
    description:
      'Talk to NVIDIA Nemotron-Labs-Audex-2B in the browser: live waveforms for your mic and the AI character, powered by an open-source speech-to-speech cog you can deploy in one click.',
  },
  '/login': {
    title: 'Sign in | app.nz',
    description: 'Sign in to run coding agents, deploy apps, manage API keys, and use the model gateway on app.nz.',
  },
  '/signup': {
    title: 'Create account | app.nz',
    description: 'Create a free app.nz account to run cloud coding agents, deploy AI apps, and route models from one place.',
  },
  '/account': {
    title: 'Account | app.nz',
    description: 'Manage credits, API keys, projects, and usage for your app.nz AI agent cloud account.',
  },
  '/api-keys': {
    title: 'API keys | app.nz',
    description: 'Manage app.nz bearer keys, provider API keys, GLM API keys, and connected ChatGPT Max plans.',
  },
  '/visualbench': {
    title: 'VisualBench | app.nz',
    description:
      'Visual regression screenshots for coding agents: capture, compare, and self-correct UI before merge on app.nz.',
  },
  '/studio': {
    title: 'Video Studio — timeline editor + render | app.nz',
    description:
      'A video editor in the browser: Canva-style stage, multi-track timeline, draggable clips, text and shapes, realtime playback, and WebM/MP4 render on app.nz.',
  },
  '/3d-editor': {
    title: '3D Studio — AI 3D editor | app.nz',
    description:
      'A 3D editor in the browser: generate models from text or images with AI credits, then simplify, smooth, voxelize, warp, paint, relight and export GLB/OBJ/STL.',
  },
  '/3d-models': {
    title: '3D Model Library — Search Thousands of Community GLBs | app.nz',
    description: 'Browse and search community GLBs and CC0 Smithsonian Open Access scans, preview them in 3D, download from R2, or open any asset in the app.nz 3D editor.',
  },
  '/animflow': {
    title: 'AnimFlow — AI animation studio with git versioning | app.nz',
    description:
      'A browser animation studio: layers, keyframe timeline, tweens and onion skinning, AI-generated motion and image layers, git-backed version history, and WebM/sprite-sheet export.',
  },
  '/animation-library': {
    title: 'Open 3D Motion, VFX & Terrain Library | app.nz',
    description:
      'Search and preview open character motion, YOLO/MediaPipe/BodyPose3D pose capture, configurable Three.js VFX, terrain recipes, and procedural object generators.',
  },
  '/manim': {
    title: 'Manim Agent — animated math explainers from a prompt | app.nz',
    description: 'Chat with a talking AI guide, generate and edit Manim Python scenes, validate them in browser WASM, render video natively, and search a public gallery by its original prompts.',
  },
  '/vfx-library': {
    title: 'Open VFX Gallery — Fire, Water, Smoke & Weather | app.nz',
    description: 'Search, preview, configure, and download source-linked Three.js VFX recipes for fire, water, smoke, impacts, energy, and weather.',
  },
  '/animation-gallery': {
    title: 'Open Character Motion & Camera Animation Gallery | app.nz',
    description: 'Search retargetable BVH and VRMA character motion plus configurable procedural camera animation.',
  },
  '/3d-object-gallery': {
    title: 'Procedural 3D Object Gallery with OBJ Downloads | app.nz',
    description: 'Preview, search, and download deterministic rocks, trees, buildings, asteroids, recipes, and OBJ geometry.',
  },
  '/image-to-3d-gallery': {
    title: 'Image to 3D Object Gallery & Reconstruction API | app.nz',
    description: 'Compare reference images with live reconstructed meshes, then download recipes and OBJ assets or call the image-to-3D API.',
  },
  '/characters': {
    title: 'AI characters | app.nz',
    description: 'Browse, create, and chat with AI characters on app.nz using selectable models, voices, knowledge files, and persistent conversations.',
  },
  '/characters-3d': {
    title: '3D AI characters | app.nz',
    description: 'Browse live 3D AI characters and chat with voiced, animated avatars on app.nz.',
  },
  '/assistants': {
    title: 'AI assistants | app.nz',
    description:
      'Chat with thousands of AI characters and assistants. Pick a model, turn on auto-thinking, edit chats, take voice calls, and hear voiced replies.',
  },
  '/art': {
    title: 'AI art gallery | app.nz',
    description: 'Explore AI-generated art and characters on app.nz.',
  },
  '/music': {
    title: 'AI music and SFX library | app.nz',
    description: 'Generate music and sound effects, then search an indexed audio library of public and personal generations on app.nz.',
  },
  '/decision-models': {
    title: 'Decision models — typed yes/no, choice and score API with probabilities | app.nz',
    description:
      'Decision models answer typed questions instead of writing prose: yes/no, multiple choice and rubric scores with probabilities, via a TypeSafe-compatible /v1/systemone API on the app.nz gateway.',
  },
  '/fusion': {
    title: 'Fusion — one prompt, N models, one best answer | app.nz',
    description:
      'Fan a prompt out across a panel of models (presets or any custom OpenRouter id), then a judge prompt fuses every candidate into one response. Save the panel as a fusion space and call it over the API.',
  },
  '/admin': {
    title: 'Admin — cloud spend | app.nz',
    description: 'Owner-only dashboard estimating cloud cost per customer with credits, plans and usage.',
  },
  '/ai': {
    title: 'AI tools for every job — staging, headshots, voiceovers & more | app.nz',
    description:
      'A directory of job-specific AI tools: virtual staging, product photos, headshots, photo restoration, art styles, logos, voiceovers, music, SFX and video. Run them with credits or call them as APIs.',
  },
  '/tools': {
    title: 'AI tools for builders | app.nz',
    description:
      'Free and AI-powered tools: prompt optimizer, bulk evals (LLM-as-a-judge), model comparison, domain availability, and a token and cost estimator, billed from one credit balance.',
  },
  '/gpu-clocker': {
    title: 'GPU Clock & Power Curve Calculator | app.nz',
    description:
      'Compare RTX 3090, 4090, 5090, L40S, A100 and H100 clock, power, throughput, temperature and perf-per-watt curves, then test your own sustained AI workload.',
  },
  '/blog': {
    title: 'Blog — models, evals & building with AI | app.nz',
    description:
      'Hands-on model comparisons, pricing breakdowns, and the eval-driven workflow we use to pick models and tune prompts on app.nz.',
  },
  '/alternatives': {
    title: 'Alternatives to cloud hosts and AI agent clouds | app.nz',
    description:
      'Compare app.nz with Vercel, Netlify, Cloudflare, AWS, OpenRouter, RunPod, Cursor, Devin, Codex Infinity, and other cloud hosting, model, and AI agent platforms.',
  },
  '/prompts': {
    title: 'AI Prompt Library for Agents, Code, Image & Video | app.nz',
    description:
      'Copy and run maintained prompts for coding agents, research, software, images, video, audio, positioning, and growth through the app.nz AI agent cloud.',
  },
  '/analytics': {
    title: 'Analytics | app.nz',
    description:
      'GA-style analytics for every site hosted on app.nz: auto-instrumented cookieless pageviews, custom events, referrers, devices, and Core Web Vitals, with an API and CLI.',
  },
  '/terms': {
    title: 'Terms of Service | app.nz',
    description: 'The terms that govern use of app.nz — the AI agent cloud by App AI NZ Ltd.',
  },
  '/privacy': {
    title: 'Privacy Policy | app.nz',
    description: 'How app.nz collects, uses, and protects your data, under the NZ Privacy Act 2020 and GDPR.',
  },
  '/providers': {
    title: 'Become a model provider | app.nz',
    description:
      'List your models on the app.nz gateway: OpenAI-compatible routing, metering handled by us, and distribution across agents, assistants, and tools. Requirements and how to apply.',
  },
  '/time-series-forecasting': {
    title: 'Time-series forecasting — Chronos-2 playground | app.nz',
    description:
      'Forecast a time series in your browser with Chronos-2: paste a column of numbers and predict up to 1024 steps ahead with calibrated confidence bands. Zero-shot, 8192-step context, ~$0.002 per forecast.',
  },
};

// Humanize a slug like "best-coding-models-2026" → "Best Coding Models 2026".
function humanizeSlug(slug: string): string {
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// Common URLs people type or that other sites/links use, mapped to the canonical
// route. Keeps /signin, /register, /sign-up, etc. from falling through to a blank
// 404 (the reported "/signin failing"). Consumed by both routing (App.tsx) and
// metaForPath so an alias still gets the right <title>.
export const ROUTE_ALIASES: Record<string, string> = {
  '/3d-library': '/3d-models',
  '/models-3d': '/3d-models',
  '/addons/postgresql': '/addons/postgres',
  '/addon': '/addons',
  '/signin': '/login',
  '/sign-in': '/login',
  '/log-in': '/login',
  '/logon': '/login',
  '/register': '/signup',
  '/sign-up': '/signup',
  '/create-account': '/signup',
  '/join': '/signup',
  '/forgot': '/forgot-password',
  '/reset': '/reset-password',
  '/whiteboard': '/draw',
  '/canvas': '/diffusion',
  '/live-draw': '/livedraw',
  '/packing': '/3d-packing',
  '/agents': '/agent',
  '/mcp': '/mcp-servers',
  '/video': '/studio',
  '/group-chats': '/group-chat',
  '/groups': '/group-chat',
  '/goals': '/agent',
  '/goal': '/agent',
  '/spreadsheet': '/sheets',
  '/audio-editor': '/audio',
  '/image-editor': '/image',
  '/photo': '/image',
  '/presentation': '/slides',
  '/svg-editor': '/vector',
  '/animation': '/animflow',
  '/manim-agent': '/manim',
  '/3d-characters': '/characters-3d',
  '/library': '/artifacts',
  '/tos': '/terms',
  '/privacy-policy': '/privacy',
  '/providers/apply': '/providers',
};

// canonicalPath resolves an alias (with any trailing slash) to its canonical
// route, strips the desktop /app mount prefix, or returns the normalized path
// unchanged.
export function canonicalPath(pathname: string): string {
  const path = stripDesktopAppBasePath(pathname).replace(/\/$/, '') || '/';
  return ROUTE_ALIASES[path] || path;
}

export function metaForPath(pathname: string): { title: string; description: string } {
  const path = canonicalPath(pathname);
  if (path === '/') return HOME_PAGE_META;
  if (STATIC_PAGE_META[path]) return STATIC_PAGE_META[path];
  if (path.startsWith('/cogs/')) {
    const showcase = cogShowcaseBySlug[path.slice('/cogs/'.length).split('/')[0]];
    if (showcase) return {
      title: `${showcase.title} Cog — input, output, API | app.nz`,
      description: `${showcase.description} See the canonical input and ${showcase.output} output, then run the same recipe through HTTP, CLI, desktop, or MCP.`,
    };
  }
  if (path.startsWith('/papers/')) return {
    title: 'Research paper | app.nz Papers',
    description: STATIC_PAGE_META['/papers'].description,
  };
  if (path.startsWith('/evals/')) {
    const slug = path.slice('/evals/'.length).split('/')[0];
    const source = evalSourceBySlug[slug];
    if (source) return {
      title: `${source.name} eval guide — run benchmarked models | app.nz`,
      description: `${source.description} Follow the evidence into hosted inference, ComfyUI workflows, and workload-specific evals on app.nz.`,
    };
  }
  // Docs section pages (/docs/notebooks) then per-endpoint pages (/docs/{slug}).
  if (path.startsWith('/docs/')) {
    const slug = path.slice('/docs/'.length).split('/')[0];
    const section = docSectionById(slug);
    if (section) {
      return {
        title: `${section.title} API | app.nz`,
        description: section.intro.length > 160 ? `${section.intro.slice(0, 157).trim()}…` : section.intro,
      };
    }
    const e = docEndpointBySlug(slug);
    if (e) {
      return {
        title: `${e.method} ${e.path} — ${e.sectionTitle} | app.nz API`,
        description: `${e.summary}. ${e.method} ${e.path} on the app.nz AI agent cloud — authenticated HTTP API reference with request examples.`,
      };
    }
  }
  // /assistants/:slug deep links share the assistants page meta.
  if (path.startsWith('/assistants/')) return STATIC_PAGE_META['/assistants'];
  if (path.startsWith('/characters-3d/')) return STATIC_PAGE_META['/characters-3d'];
  // /spaces/:slug — registry-driven model spaces (custom spaces hit the exact
  // STATIC_PAGE_META entries above before reaching here).
  if (path.startsWith('/spaces/')) {
    const m = spaceMeta(path.slice('/spaces/'.length).split('/')[0]);
    if (m) return m;
  }
  // Catalog detail routes carry unique, content-derived metadata.
  // /ai/:slug use-case landing pages carry unique catalog-driven meta.
  if (path.startsWith('/ai/')) {
    const slug = path.slice('/ai/'.length).split('/')[0];
    return useCaseMeta(slug) ?? STATIC_PAGE_META['/ai'];
  }
  if (path.startsWith('/tools/')) {
    const slug = path.slice('/tools/'.length).split('/')[0];
    const tool = toolBySlug[slug];
    if (tool) {
      return {
        title: `${tool.title} — ${tool.category} tool | app.nz`,
        description: tool.description,
      };
    }
    return { title: `${humanizeSlug(slug)} — Tools | app.nz`, description: STATIC_PAGE_META['/tools'].description };
  }
  if (path.startsWith('/blog/')) {
    const slug = path.slice('/blog/'.length).split('/')[0];
    const post = blogPosts.find((item) => item.slug === slug);
    if (post) return { title: `${post.title} | app.nz Blog`, description: post.description };
    return { title: `${humanizeSlug(slug)} | app.nz Blog`, description: STATIC_PAGE_META['/blog'].description };
  }
  if (path.startsWith('/alternatives/')) {
    const slug = path.slice('/alternatives/'.length).split('/')[0];
    const alt = alternativeBySlug[slug];
    if (alt) {
      return {
        title: alt.title,
        description: alt.excerpt,
      };
    }
    return { title: `${humanizeSlug(slug)} Alternative | app.nz`, description: STATIC_PAGE_META['/alternatives'].description };
  }
  if (path.startsWith('/prompts/category/')) {
    const slug = path.slice('/prompts/category/'.length).split('/')[0];
    const category = promptLibraryJSON.categories.find((item) => item.slug === slug);
    if (category) return { title: `${category.name} Prompts | app.nz`, description: category.description };
  }
  if (path.startsWith('/prompts/type/')) {
    const slug = path.slice('/prompts/type/'.length).split('/')[0];
    const label = slug === 'audio' ? 'Audio' : slug === 'video' ? 'Video' : slug === 'image' ? 'Image' : 'Text';
    return { title: `${label} Prompts | app.nz`, description: `Copy-ready ${label.toLowerCase()} prompts tuned for app.nz model routes and generation spaces.` };
  }
  if (path.startsWith('/prompts/')) {
    const slug = path.slice('/prompts/'.length).split('/')[0];
    const prompt = libraryPromptBySlug[slug];
    if (prompt) return { title: `${prompt.title} Prompt | app.nz`, description: prompt.summary };
  }

  const route = platformRouteByPath[path];
  if (route) {
    return {
      title: `${route.label} | app.nz`,
      description: route.description,
    };
  }

  return HOME_PAGE_META;
}

// Interactive routes render client-only (they read auth, window, and live data),
// so prerendering them would risk hydration mismatches. Everything else is static
// marketing content that benefits from per-route prerendered HTML for SEO.
const CLIENT_ONLY_PATHS = new Set<string>([
  '/agent',
  '/browser-agent',
  '/3d-packing',
  '/search',
  '/search/web',
  '/search/index',
  '/search/agent',
  '/deep-research',
  '/skills',
  '/datasets',
  '/notebooks',
  '/dashboards',
  '/draw',
  '/diffusion',
  '/livedraw',
  '/sheets',
  '/artifacts',
  '/repos',
  '/cogs',
  '/training',
  '/comfy',
  '/spaces/splat',
  '/spaces/fast-video',
  '/builds',
  '/sites',
  '/visualbench',
  '/characters',
  '/characters-3d',
  '/assistants',
  '/group-chat',
  '/art',
  '/time-series-forecasting',
  '/account',
  '/api-keys',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/admin',
]);

// prerenderPaths is the list of routes baked to their own dist/<path>/index.html
// with per-route <title>/<meta>. The homepage plus every static marketing route.
export function prerenderPaths(): string[] {
  const paths = ['/', '/apps', '/games/neon-relay', '/games/orbit-words', '/games/reword', '/games/big-multiplayer-chess', '/games/word-smashing', '/games/multiplication-master', '/games/signal-orchard', '/games/cartographers-kitchen', '/games/museum-after-midnight', '/docs', '/evals', ...evalSources.map((source) => `/evals/${source.slug}`), '/investors', '/investors/vision', '/investors/deep-dive', '/addons', '/addons/gobed', '/addons/postgres', '/products/papers', '/rl', '/enterprise', '/security'];
  for (const route of platformRoutes) {
    // Canonicalize so a route registered under an alias (e.g. /agents → /agent)
    // still matches the client-only exclusion list.
    if (!CLIENT_ONLY_PATHS.has(canonicalPath(route.path)) && !CLIENT_ONLY_PATHS.has(route.path) && !paths.includes(route.path)) {
      paths.push(route.path);
    }
  }
  // Docs section pages, then per-endpoint API reference pages — static, baked for SEO.
  for (const id of docSectionIds) {
    paths.push(`/docs/${id}`);
  }
  for (const e of docEndpoints) {
    paths.push(`/docs/${e.slug}`);
  }
  // AI use-case landing pages — the /ai directory plus one page per use case.
  paths.push('/ai');
  for (const u of useCases) {
    paths.push(`/ai/${u.slug}`);
  }
  // Comparison landing pages for cloud hosts, model infrastructure, and AI agents.
  paths.push('/alternatives');
  for (const alt of alternatives) {
    paths.push(`/alternatives/${alt.slug}`);
  }
  paths.push('/prompts');
  for (const category of promptLibraryJSON.categories) paths.push(`/prompts/category/${category.slug}`);
  for (const type of ['text', 'image', 'video', 'audio']) paths.push(`/prompts/type/${type}`);
  for (const prompt of libraryPrompts) paths.push(`/prompts/${prompt.slug}`);
  // Blog pages are static editorial content. Streaming SSR resolves their lazy
  // chunks before writing HTML, so crawlers receive the real article and H1.
  paths.push('/blog');
  for (const post of blogPosts) paths.push(`/blog/${post.slug}`);
  paths.push('/mcp-servers');
  paths.push('/cogs/examples');
  paths.push(...cogShowcasePaths);
  return paths;
}

export function indexableShellPaths(): string[] {
  return [
    // Public product surfaces which need crawlable canonical shells even when
    // their live-data/browser dependencies make full SSR inappropriate.
    '/integrations', '/tools', '/gpu-clocker', '/papers', '/characters', '/characters-3d', '/art',
    '/search', '/search/web', '/search/index', '/search/agent', '/deep-research',
    '/repos', '/skills', '/skills/market', '/datasets', '/notebooks', '/dashboards',
    '/cogs', '/builds', '/ci', '/visualbench', '/schedulers', '/browser-agent',
    '/agents-sdk', '/social', '/auto-agents', '/monitors', '/analytics', '/training', '/comfy',
    '/worlds', '/animation-library', '/animation-gallery', '/vfx-library', '/3d-models',
    '/3d-object-gallery', '/image-to-3d-gallery', '/animflow', '/music',
    '/3d-editor', '/diffusion', '/livedraw', '/audio', '/image', '/slides', '/vector',
    '/spaces/creator-lab', '/spaces/blender-lab', '/spaces/unreal-lab',
    '/video-extension', '/text-to-image', '/image-to-3d', '/text-to-3d',
    '/media-optimizer', '/time-series-forecasting', '/playground', '/fusion', '/decision-models',
    '/terms', '/privacy', '/providers',
    ...toolSlugs.map((slug) => `/tools/${slug}`),
  ];
}

// spaceIndexablePaths lists every /spaces route (index, registry spaces, and the
// hand-built custom spaces) for prerendered shells + the sitemap.
export function spaceIndexablePaths(): string[] {
  return [
    '/spaces',
    ...spaces.map((s) => `/spaces/${s.slug}`),
    '/spaces/fast-vfx',
    '/spaces/media-services',
    '/spaces/sam-mask-adjust',
    '/spaces/audio-to-audio',
    '/spaces/abot-world',
    '/spaces/ardy',
    '/spaces/splat',
    '/spaces/fast-video',
  ];
}


// isPrerenderedPath reports whether the client can safely hydrate a prerendered
// route. The marketplace and arcade are intentionally lazy interactive
// boundaries: their server stream and client chunk resolution do not produce
// the same Suspense markers, so keep their complete HTML for crawlers but use a
// clean client mount to avoid React #418 and duplicated event trees.
export function isPrerenderedPath(pathname: string): boolean {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/apps' || path.startsWith('/games/')) return false;
  return prerenderPaths().includes(path);
}
